import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';
import SeverityChip from './SeverityChip';

const ScheduleTab: React.FC = () => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin-schedule'],
    queryFn: adminAPI.getSchedule,
    staleTime: 30000,
    refetchInterval: 30000,
  });

  const [confirmJob, setConfirmJob] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const trigger = useMutation({
    mutationFn: (jobName: string) => adminAPI.triggerJob(jobName),
    onSuccess: (resp) => {
      setLastResult(`Triggered ${resp.job_name} — ${resp.note}`);
      qc.invalidateQueries({ queryKey: ['admin-schedule'] });
      qc.invalidateQueries({ queryKey: ['admin-runs'] });
    },
    onError: (err: any) => {
      setLastResult(`Error: ${err?.response?.data?.detail || err?.message || 'unknown'}`);
    },
  });

  const jobs = data?.jobs || [];

  const fmtAgo = (iso?: string | null) => {
    if (!iso) return '—';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
    if (ms < 3_600_000) return `${Math.floor(ms / 60000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  };

  return (
    <Panel label="ETL SCHEDULE">
      {lastResult && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 2, color: lastResult.startsWith('Error') ? '#ff3344' : '#00ff41' }}
        >
          {lastResult}
        </Typography>
      )}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>JOB</TableCell>
              <TableCell>CRON</TableCell>
              <TableCell>ENABLED</TableCell>
              <TableCell align="right">RUNS</TableCell>
              <TableCell align="right">ERRORS</TableCell>
              <TableCell align="right">CONSEC FAILS</TableCell>
              <TableCell>LAST RUN</TableCell>
              <TableCell>LAST SUCCESS</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((j: any) => {
              const severity =
                j.consecutive_failures > 3 ? 'CRITICAL'
                  : j.consecutive_failures > 0 ? 'MAJOR'
                  : j.enabled ? 'OK' : 'MINOR';
              return (
                <TableRow key={j.job_name} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{j.job_name}</Typography>
                    <Typography variant="caption" color="textSecondary">{j.job_type}</Typography>
                  </TableCell>
                  <TableCell><code>{j.cron_string}</code></TableCell>
                  <TableCell><SeverityChip severity={severity} /></TableCell>
                  <TableCell align="right">{j.run_count ?? 0}</TableCell>
                  <TableCell align="right">{j.error_count ?? 0}</TableCell>
                  <TableCell align="right">
                    {j.consecutive_failures > 0 ? (
                      <Typography color="error" fontWeight={600}>{j.consecutive_failures}</Typography>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{fmtAgo(j.last_run_at)}</TableCell>
                  <TableCell>{fmtAgo(j.last_success_at)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!j.enabled || trigger.isPending}
                      onClick={() => setConfirmJob(j.job_name)}
                    >
                      [ TRIGGER ]
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!confirmJob} onClose={() => setConfirmJob(null)}>
        <DialogTitle>Trigger {confirmJob}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This rolls back last_run_at by 24h so the scheduler picks the job up within ~60 seconds.
            Queued jobs cannot be cancelled from this UI.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmJob(null)}>[ CANCEL ]</Button>
          <Button
            onClick={() => {
              if (confirmJob) trigger.mutate(confirmJob);
              setConfirmJob(null);
            }}
            variant="outlined"
            color="warning"
          >
            [ CONFIRM TRIGGER ]
          </Button>
        </DialogActions>
      </Dialog>
    </Panel>
  );
};

export default ScheduleTab;
